import React from "react";
import PropTypes from "prop-types";

/**
 * Custom React Hook: useWindowDimensions
 *
 * Description:
 * This hook provides the current dimensions of the browser window, updating dynamically on window resize.
 *
 * @returns {{ width: number, height: number }} - An object containing the current window width and height.
 */
function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
}

/**
 * Custom React Hook: addNewHistoryLine
 *
 * Description:
 * This hook assigns a history line to the job with the current date / time.
 *
 * @returns {{ jobState: node }} - An object containing the current job history.
 */
export function addNewHistoryLine({ jobState, historyLine }) {
    jobState.History = jobState.History || [];
    jobState.History.push({ ...historyLine, Date: new Date(Date.now()).toUTCString() });
    return jobState.History; // Return if required
}

addNewHistoryLine.propTypes = {
    jobState: PropTypes.node.isRequired,
    historyLine: PropTypes.node.isRequired,
};

/**
 * Custom React Hook: useWindowDimensions
 *
 * Description:
 * This hook returns an object containing the current dimensions of the browser window.
 * It automatically updates on window resize.
 *
 * @returns {{ width: number, height: number }} - An object containing the current window width and height.
 */
export function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = React.useState(getWindowDimensions());

    React.useEffect(() => {
        /**
         * Event handler for window resize
         */
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        // Attach event listener for window resize
        window.addEventListener("resize", handleResize);

        // Detach event listener on component unmount
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowDimensions;
}

useWindowDimensions.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
};

/**
 * Function: getD365CustomerCodes
 *
 * Description:
 * This function retrieves D365 customer codes based on provided parameters and if the state is active.
 *
 * @param {Object} options - An object containing parameters for retrieving D365 customer codes.
 * @param {Object} options.job - The job object.
 * @param {Object} options.app - The app object.
 * @param {Object} options.config - The config object.
 * @param {Function} options.dispatch - The dispatch function for updating state.
 * @param {Function} options.updateCustomer - The function to update customer information.
 * @param {string} options.get_tier - The tier to be retrieved.
 * @returns {string} - The D365 customer code.
 */
export function getD365CustomerCodes({ job, app, config, dispatch, updateCustomer, get_tier }) {
    // Retrieve the current region configuration
    const currentRegion = app?.offerings?.[window.brand]?.Regions?.[config?.region?.id]?.region;
    let update_tier;

    /**
     * Helper Function: findTierInCategory
     *
     * Finds the tier in a specific category within the pricing_accounts object.
     *
     * @param {string} category - The category to search in.
     * @returns {Object | undefined} - The tier object if found, otherwise undefined.
     */
    const findTierInCategory = (category) => {
        return currentRegion?.pricing_accounts?.[category]?.find((obj) => obj["CUST CODE"] === get_tier);
    };

    if (currentRegion?.D365_State_Active === true) {
        // If D365 state is active, search through each pricing_accounts category
        for (const category of Object.keys(currentRegion.pricing_accounts ?? {})) {
            const foundTier = findTierInCategory(category);
            if (foundTier?.D365_CODE) {
                update_tier = foundTier;
                dispatch(updateCustomer("DistLevel", update_tier.D365_CODE));
                break;
            }
        }
    } else {
        // If D365 state is inactive, search DMAPSTORE for the corresponding Lyves code
        const dMapStore = currentRegion?.pricing_accounts?.["DMAPSTORE"] ?? [];
        const matchedTier = dMapStore.find((tier) => tier["D365_CODE"] === job?.Customer?.DistLevel);
        if (matchedTier) {
            dispatch(updateCustomer("DistLevel", matchedTier["CUST CODE"]));
        }
    }

    // Return the found CUST CODE if update_tier exists, otherwise return the original get_tier
    return update_tier?.["CUST CODE"] || get_tier;
}

getD365CustomerCodes.propTypes = {
    job: PropTypes.object,
    app: PropTypes.object,
    config: PropTypes.object,
    dispatch: PropTypes.func,
    updateCustomer: PropTypes.func,
    get_tier: PropTypes.string,
};

/**
 * Function: convertValueLabels
 *
 * Description:
 * Converts an array of strings into an array of objects with the same value and label.
 *
 * @param {string[]} values - An array of strings to be converted.
 * @returns {Object[]} - An array of objects with value and label properties.
 */
export const convertValueLabels = (values) => {
    return values.map((v) => {
        return { value: v, label: v };
    });
};

// Prop types for convertValueLabels function
convertValueLabels.propTypes = {
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
};
